import React, { useState } from 'react';
import { Wand2, Building, DollarSign, Download, Shield, ArrowRight, Image, Upload, FileText, Package, Calendar, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import toast from 'react-hot-toast';
import { geminiImageService } from '@/services/gemini-image-generation';
import { bimExportService } from '@/services/bim-export';

export function SimpleArchitectural() {
  const [prompt, setPrompt] = useState('');
  const [projectType, setProjectType] = useState('residential');
  const [style, setStyle] = useState('modern');
  const [squareFootage, setSquareFootage] = useState('2000-3000');
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);

  const handleGenerate = async () => {
    if (!prompt.trim() || prompt.length < 20) {
      toast.error('Please provide a more detailed description (at least 20 characters)');
      return;
    }

    setIsGenerating(true);
    
    try {
      const fullPrompt = `
        Create a comprehensive architectural design package for:
        ${prompt}
        
        Project Details:
        - Type: ${projectType}
        - Style: ${style}  
        - Size: ${squareFootage} square feet
        
        Generate:
        1. Floor plan with room layouts and dimensions
        2. Exterior elevation drawing
        3. Interior 3D perspective rendering
        4. Cost estimate breakdown
        5. Building code compliance notes
        
        Present this as a professional architectural proposal with detailed descriptions for each component.
      `;

      console.log('Starting generation process...');
      
      // Generate realistic architectural images using Gemini's native image generation
      console.log('Generating realistic architectural visualizations with Gemini...');
      const detailedPrompt = `${prompt}. Project type: ${projectType}. Architectural style: ${style}. Size: ${squareFootage} square feet.`;
      
      const imagePromises = [
        geminiImageService.generateFloorPlan(detailedPrompt, { width: 1024, height: 768 }).then(img => {
          console.log('Floor plan generated:', img ? 'Success' : 'Failed');
          return img;
        }),
        geminiImageService.generateElevation(detailedPrompt, 'front', style).then(img => {
          console.log('Elevation generated:', img ? 'Success' : 'Failed');
          return img;
        }),
        geminiImageService.generate3DRendering(detailedPrompt, 'afternoon').then(img => {
          console.log('3D rendering generated:', img ? 'Success' : 'Failed');
          return img;
        })
      ];

      // Call API for text content - ALWAYS use direct API
      console.log('Calling Gemini API directly (no Cloudflare Function)...');
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=AIzaSyDlnuuF39oDeZRaZF7JtILBZ1JgbCTwFqc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: fullPrompt
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 8192,
            }
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      // Wait for both text and images to complete
      console.log('Waiting for text and images...');
      const [data, floorPlanImage, elevationImage, renderingImage] = await Promise.all([
        response.json(),
        ...imagePromises
      ]);
      
      console.log('API Response:', data);
      console.log('Generated images:', {
        floorPlan: floorPlanImage ? 'Generated' : 'Missing',
        elevation: elevationImage ? 'Generated' : 'Missing', 
        rendering: renderingImage ? 'Generated' : 'Missing'
      });
      
      // Extract text content
      const content = data.candidates?.[0]?.content?.parts?.[0]?.text || data.text || 'Generation completed';
      
      // Parse the content into sections
      const sections = {
        floorPlan: extractSection(content, 'floor plan'),
        elevation: extractSection(content, 'elevation'),
        rendering: extractSection(content, '3d|rendering|interior'),
        cost: extractSection(content, 'cost|budget|estimate'),
        compliance: extractSection(content, 'code|compliance|building')
      };

      setResults({
        content,
        sections,
        images: {
          floorPlan: floorPlanImage,
          elevation: elevationImage,
          rendering: renderingImage
        },
        timestamp: new Date().toISOString()
      });

      toast.success('Architectural design with images generated successfully!');
      
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate design. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const extractSection = (text: string, keywords: string) => {
    const keywordRegex = new RegExp(`(${keywords}).*?(?=\\n\\n|$)`, 'is');
    const match = text.match(keywordRegex);
    return match ? match[0] : '';
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setUploadedImages(Array.from(files));
      toast.success(`${files.length} image(s) uploaded`);
    }
  };

  const downloadResults = () => {
    if (!results) return;
    
    const blob = new Blob([results.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `architectural-design-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBIMExport = async (format: 'dxf' | 'ifc' | 'pdf') => {
    if (!results) return;

    try {
      const exportData = {
        projectName: prompt.split(' ').slice(0, 3).join(' ') || 'Architecture Project',
        description: prompt,
        floorPlan: results.sections.floorPlan || '',
        elevation: results.sections.elevation || '',
        rendering: results.sections.rendering || '',
        costEstimate: results.sections.cost || {},
        compliance: results.sections.compliance || {},
        images: results.images || { floorPlan: '', elevation: '', rendering: '' }
      };

      let exportResult;
      switch (format) {
        case 'dxf':
          exportResult = await bimExportService.exportToDXF(exportData);
          break;
        case 'ifc':
          exportResult = await bimExportService.exportToIFC(exportData);
          break;
        case 'pdf':
          exportResult = await bimExportService.exportToPDF(exportData);
          break;
      }

      bimExportService.downloadExport(exportResult);
      toast.success(`${format.toUpperCase()} file downloaded successfully!`);
    } catch (error) {
      console.error('BIM export error:', error);
      toast.error('Failed to export BIM file. Please try again.');
    }
  };

  const handleBIMPackageExport = async () => {
    if (!results) return;

    try {
      const exportData = {
        projectName: prompt.split(' ').slice(0, 3).join(' ') || 'Architecture Project',
        description: prompt,
        floorPlan: results.sections.floorPlan || '',
        elevation: results.sections.elevation || '',
        rendering: results.sections.rendering || '',
        costEstimate: results.sections.cost || {},
        compliance: results.sections.compliance || {},
        images: results.images || { floorPlan: '', elevation: '', rendering: '' }
      };

      const exports = await bimExportService.generateBIMPackage(exportData);
      exports.forEach(exportResult => {
        bimExportService.downloadExport(exportResult);
      });
      
      toast.success('Complete BIM package downloaded successfully!');
    } catch (error) {
      console.error('BIM package export error:', error);
      toast.error('Failed to export BIM package. Please try again.');
    }
  };

  const handleBookConsultation = () => {
    // Simulate booking a consultation with an architect
    toast.success('Consultation booking form would open here! Connect with our professional architects.');
    // In a real implementation, this would open a calendar booking widget
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Building className="w-10 h-10 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Architecture AI</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Describe your vision and get a complete architectural design package in seconds
          </p>
        </div>

        {!results ? (
          /* Input Form */
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" />
                  Describe Your Project
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Project Description</Label>
                  <Textarea
                    placeholder="Example: I want to renovate my 1960s ranch house into a modern open-concept home with a large kitchen island, floor-to-ceiling windows, and a master suite addition..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="min-h-[120px]"
                  />
                  <div className="text-sm text-gray-500">
                    {prompt.length}/20 characters minimum
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Upload Reference Images (Optional)</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      id="image-upload"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload images of your existing space or reference photos
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        JPG, PNG up to 10MB each
                      </span>
                    </label>
                  </div>
                  {uploadedImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {uploadedImages.map((file, index) => (
                        <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Project Type</Label>
                    <Select value={projectType} onValueChange={setProjectType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="renovation">Renovation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Architectural Style</Label>
                    <Select value={style} onValueChange={setStyle}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="modern">Modern</SelectItem>
                        <SelectItem value="traditional">Traditional</SelectItem>
                        <SelectItem value="contemporary">Contemporary</SelectItem>
                        <SelectItem value="minimalist">Minimalist</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Size (sq ft)</Label>
                    <Select value={squareFootage} onValueChange={setSquareFootage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1000-1500">1,000 - 1,500</SelectItem>
                        <SelectItem value="1500-2000">1,500 - 2,000</SelectItem>
                        <SelectItem value="2000-3000">2,000 - 3,000</SelectItem>
                        <SelectItem value="3000-5000">3,000 - 5,000</SelectItem>
                        <SelectItem value="5000+">5,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Alert>
                  <Shield className="w-4 h-4" />
                  <AlertDescription>
                    Your project data is processed securely and never shared.
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full" 
                  size="lg"
                  disabled={!prompt.trim() || prompt.length < 20 || isGenerating}
                  onClick={handleGenerate}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Generating Your Design...
                    </>
                  ) : (
                    <>
                      Generate Complete Design Package
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Results Display */
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Your Architectural Design Package</h2>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setResults(null)}>
                  Create New Design
                </Button>
                <Button onClick={downloadResults} className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Text Report
                </Button>
                <Button onClick={() => handleBIMExport('pdf')} variant="outline" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  PDF
                </Button>
                <Button onClick={() => handleBIMExport('dxf')} variant="outline" className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  DXF
                </Button>
                <Button onClick={() => handleBIMExport('ifc')} variant="outline" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  IFC
                </Button>
                <Button onClick={handleBIMPackageExport} className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Complete BIM Package
                </Button>
              </div>
            </div>

            <div className="grid gap-6">
              {/* Generated Images */}
              {results.images && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Generated Architectural Visualizations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <h4 className="font-semibold mb-2">Floor Plan</h4>
                        <img 
                          src={results.images.floorPlan} 
                          alt="Generated floor plan"
                          className="w-full h-48 object-contain border rounded-lg bg-white"
                          onError={(e) => {
                            e.currentTarget.src = "data:image/svg+xml;base64," + btoa(`
                              <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
                                <rect width="100%" height="100%" fill="#f8f8f8"/>
                                <text x="200" y="150" font-family="Arial" font-size="16" text-anchor="middle" fill="#666">Floor Plan Loading...</text>
                              </svg>
                            `);
                          }}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Elevation View</h4>
                        <img 
                          src={results.images.elevation} 
                          alt="Generated elevation"
                          className="w-full h-48 object-contain border rounded-lg bg-white"
                          onError={(e) => {
                            e.currentTarget.src = "data:image/svg+xml;base64," + btoa(`
                              <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
                                <rect width="100%" height="100%" fill="#f8f8f8"/>
                                <text x="200" y="150" font-family="Arial" font-size="16" text-anchor="middle" fill="#666">Elevation Loading...</text>
                              </svg>
                            `);
                          }}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">3D Rendering</h4>
                        <img 
                          src={results.images.rendering} 
                          alt="Generated 3D rendering"
                          className="w-full h-48 object-contain border rounded-lg bg-white"
                          onError={(e) => {
                            e.currentTarget.src = "data:image/svg+xml;base64," + btoa(`
                              <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
                                <rect width="100%" height="100%" fill="#f8f8f8"/>
                                <text x="200" y="150" font-family="Arial" font-size="16" text-anchor="middle" fill="#666">3D Rendering Loading...</text>
                              </svg>
                            `);
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {results.sections.floorPlan && (
                <Card>
                  <CardHeader>
                    <CardTitle>Floor Plan Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded">
                        {results.sections.floorPlan}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}

              {results.sections.elevation && (
                <Card>
                  <CardHeader>
                    <CardTitle>Elevation Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded">
                        {results.sections.elevation}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}

              {results.sections.cost && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Cost Estimate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded">
                        {results.sections.cost}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Professional Consultation */}
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Users className="w-5 h-5" />
                    Ready to Take It Further?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-blue-700">
                    Get your AI-generated design reviewed and refined by our professional architects. 
                    We'll help you turn this concept into detailed construction-ready plans.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold mb-2">What You'll Get:</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Professional design review</li>
                        <li>• Detailed construction drawings</li>
                        <li>• Building permit assistance</li>
                        <li>• Cost optimization strategies</li>
                        <li>• Code compliance verification</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold mb-2">Consultation Options:</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• 1-hour video consultation - $150</li>
                        <li>• Design review package - $500</li>
                        <li>• Full project development - Custom quote</li>
                      </ul>
                    </div>
                  </div>
                  <Button onClick={handleBookConsultation} className="w-full flex items-center justify-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Book Professional Consultation
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Complete Design Report</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-gray max-w-none">
                    <div 
                      className="bg-white p-6 rounded border text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: results.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
                          .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
                          .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
                          .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
                          .replace(/\n\n/g, '</p><p class="mb-3">')
                          .replace(/^(.)/gm, '<p class="mb-3">$1')
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}